import { createContext, type FC, type PropsWithChildren, useCallback, useContext } from "react";

import { resolveAuthHref, type ResolveAuthHrefOptions } from "@jti/fe-common/routes/auth-routes";

type AuthRoutingContextValue = {
  authBasePath: string;
  isAppRouterEnabled: boolean;
};

export type AuthRoutingProviderProps = PropsWithChildren<{
  authBasePath: string;
  isAppRouterEnabled: boolean;
}>;

type DistributiveOmit<Type, Keys extends PropertyKey> = Type extends unknown ? Omit<Type, Keys> : never;

export type ResolveConfiguredAuthHrefOptions = DistributiveOmit<
  ResolveAuthHrefOptions,
  "authBasePath" | "authPageUrl" | "isAppRouterEnabled"
>;

const AuthRoutingContext = createContext<AuthRoutingContextValue | undefined>(undefined);

export const AuthRoutingProvider: FC<AuthRoutingProviderProps> = ({ authBasePath, isAppRouterEnabled, children }) => (
  <AuthRoutingContext.Provider value={{ authBasePath, isAppRouterEnabled }}>{children}</AuthRoutingContext.Provider>
);

const useAuthRouting = (): AuthRoutingContextValue => {
  const authRouting = useContext(AuthRoutingContext);

  if (authRouting === undefined) {
    throw new Error("Auth routing hooks must be used within AuthRoutingProvider");
  }

  return authRouting;
};

export const useAuthHrefResolver = () => {
  const { authBasePath, isAppRouterEnabled } = useAuthRouting();

  return useCallback(
    (options: ResolveConfiguredAuthHrefOptions): string => {
      if (options.predefinedHref !== undefined) {
        return resolveAuthHref(options);
      }

      return resolveAuthHref({ ...options, authBasePath, isAppRouterEnabled });
    },
    [authBasePath, isAppRouterEnabled]
  );
};

export const useIsAuthPage = (pathname: string): boolean => {
  const { authBasePath } = useAuthRouting();
  const normalizedAuthBasePath = authBasePath.replace(/\/$/, "");

  return pathname === normalizedAuthBasePath || pathname.startsWith(`${normalizedAuthBasePath}/`);
};
